import { ResizeObserver } from '@juggle/resize-observer';
import { Grid } from '@material-ui/core';
import React, { useEffect, useRef, useState } from 'react';
import { useEventListener } from 'utils/use-event-listener';
import { TableScrollbarProps } from './table-scrollbar-props';
import './table-scrollbar.scss';

export const TableScrollbar: React.FC<TableScrollbarProps> = (props) => {
  const {
    tableRef,
    stickyHeaderRef,
  } = props;
  const [fullWidth, setFullWidth] = useState<any>();
  const [width, setWidth] = useState<any>();
  const [isSticky, setIsSticky] = useState<boolean>(false);
  const [isTableVisible, setIsTableVisible] = useState<boolean>(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const scrollingBar = useRef<HTMLDivElement>(null);
  let scrollTimeout: any;

  /**
   * Shows or hides the left/right scroll shadow pseudoelement based
   * on the scrollLeft value.
   *
   * TODO: Refactor to generic table component
   * @param event
   */
  const addShadowWhenScrolled = (e: any, elRef: any) => {
    if (e.target.scrollLeft === 0 && elRef) {
      elRef.classList.remove('table-scroll-shadow--left');
    } else {
      elRef.classList.add('table-scroll-shadow--left');
    }

    if (e.target.scrollLeft + e.target.offsetWidth >= e.target.scrollWidth) {
      elRef.classList.remove('table-scroll-shadow--right');
    } else {
      elRef.classList.add('table-scroll-shadow--right');
    }
  };

  /**
   * Called when either the table or sticky scrollbar scrolls.
   *
   * Sets the target element (either table or sticky scrollbar) and
   * `stickyHeaderRef` (sticky header) scroll values to keep them aligned.
   * @param e
   * @param targetElement
   */
  const tableScroll = (e: any, targetElement: HTMLDivElement | null) => {
    const { target } = e;
    clearTimeout(scrollTimeout);
    // Use a timeout to ensure changing scroll values aren't spammed
    // from both scrollbars changing.
    scrollTimeout = setTimeout(() => {
      // eslint-disable-next-line no-unused-expressions
      targetElement?.scroll(target.scrollLeft, 0);
      let stickyHeaderTarget;
      // if there is no header reference, scroll available header in page
      // use case with two tables and one sticky header
      if (!stickyHeaderRef.current) {
        const availableStickyHeader = document.getElementsByClassName('sticky-header')[0];
        const targetSticky = availableStickyHeader.getElementsByClassName('sg-table__table')[0];
        stickyHeaderTarget = targetSticky;
      } else {
        stickyHeaderTarget = stickyHeaderRef.current;
      }
      stickyHeaderTarget.parentElement.scroll(target.scrollLeft, 0);
      addShadowWhenScrolled(e, tableRef.current);
      addShadowWhenScrolled(e, stickyHeaderTarget.parentElement);
    });
  };

  useEventListener('scroll', (e: any) => tableScroll(e, tableContainerRef.current), tableRef.current);
  useEventListener('scroll', (e: any) => tableScroll(e, tableRef.current), tableContainerRef.current);

  /**
 * Sets full width for scrollbar and width of container that will be scrolled.
 */
  useEffect(() => {
    setFullWidth(props.tableRef.current?.scrollWidth);
    setWidth(props.tableRef.current?.offsetWidth);
  }, [props]);

  /**
   * Hides/shows table scrollbar if end of table is not visible.
   */
  useEffect(() => {
    const scrollBar = scrollingBar.current;
    const observer = new IntersectionObserver(([entry]) => {
      setIsSticky(!entry.isIntersecting);
    }, { threshold: 0.75 });

    observer.observe(scrollBar as HTMLElement);

    return () => {
      observer.unobserve(scrollBar as HTMLElement);
    };
  }, [scrollingBar]);

  /**
   * Hides sticky scroll if table is not visible.
   */
  useEffect(() => {
    const table = props.tableRef.current;
    const observer = new IntersectionObserver(([entry]) => {
      setIsTableVisible(entry.isIntersecting);
    }, { threshold: 0 });
    observer.observe(table as HTMLElement);

    return () => {
      observer.unobserve(table as HTMLElement);
    };
  }, [props.tableRef]);

  /**
   * Update scrollbar width on window resize.
   */
  useEffect(() => {
    const observer = new ResizeObserver(() => {
      setWidth(props.tableRef.current.offsetWidth);
      setFullWidth(props.tableRef.current.scrollWidth);

      const table = props.tableRef.current;
      const tableHeader = props.stickyHeaderRef?.current;
      // Add the scroll--right class when the table scroll width changes.
      // Mainly used for initially setting the class if the table has a scrollbar.
      if (table.scrollWidth > table.offsetWidth && table.scrollLeft + table.offsetWidth < table.scrollWidth) {
        table.classList.add('table-scroll-shadow--right');
        if (tableHeader) {
          tableHeader.parentElement.classList.add('table-scroll-shadow--right');
        }
      } else {
        table.classList.remove('table-scroll-shadow--right');
        if (tableHeader) {
          tableHeader.parentElement.classList.remove('table-scroll-shadow--right');
        }
      }
    });
    observer.observe(props.tableRef.current as HTMLElement);

    return () => {
      observer.disconnect();
    };
  }, [props.stickyHeaderRef, props.tableRef]);

  return (
    <div
      className={`sg-table-scrollbar ${isSticky && isTableVisible ? 'sg-table-scrollbar--sticky' : ''}`}
      ref={scrollingBar}
    >
      <Grid
        container
        item
        xs={12}
        className="sg-table-scrollbar__wrapper"
        style={{ width }}
      >
        <Grid
          container
          item
          xs={12}
          className="sg-table-scrollbar__container"
          style={{ width }}
          ref={tableContainerRef}
        >
          <div
            className="sg-table-scrollbar__scroll"
            style={{ minWidth: fullWidth }}
          />
        </Grid>
      </Grid>
    </div>
  );
};
